/**
 * FinancialSubscriptions - Página de Recorrência / Assinaturas do módulo Financeiro
 *
 * Layout espelhado em FinancialInstallments.tsx (mesmos KPIs horizontais
 * compactos, barra de filtros, tabela premium e paginação server-side).
 *
 * 100% server-side via RPC:
 *   - Lista: useFinanceiroAssinaturasPaginated → get_assinaturas_paginated
 *   - KPIs: já vêm na primeira row (COUNT(*) OVER())
 *   - Realtime: invalida cache em financeiro_assinaturas e financeiro_cobrancas
 *
 * @author Módulo Financeiro - Recorrência
 * @version 1.0.0
 * RELEASE: 2026-06-09 v1.0.0 - Página implementada (Fase 5 do módulo Recorrência)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { cn, parseDateLocal } from '@/lib/utils';
import { formatDocumentForDisplay } from '@/lib/input-masks';
import { useAuth } from '@/hooks/useAuth';
import { useResourceLimits } from '@/hooks/useResourceLimits';
import { useRefreshWithTimestamp } from '@/hooks/useRefreshWithTimestamp';
import { createLogger } from '@/lib/logger';
import {
  useFinanceiroAssinaturasPaginated,
  AssinaturaPaginatedFilters,
  PaginatedAssinatura,
} from '@/hooks/useFinanceiroAssinaturasPaginated';
import { useFinanceiroMeiosPagamento, TIPOS_PAGAMENTO } from '@/hooks/useFinanceiroMeiosPagamento';
import { FreeTierLimitBanner } from '@/components/financial/FreeTierLimitBanner';

import {
  Repeat,
  CheckCircle2,
  Clock,
  TrendingUp,
  Eye,
  Search,
  X,
  MoreHorizontal,
  RefreshCw,
  Plus,
  AlertTriangle,
  Crown,
  Package,
  PauseCircle,
  PlayCircle,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollIndicators } from '@/components/ui/scroll-indicators';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAutoOpenFromQuery } from '@/hooks/useAutoOpenFromQuery';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CreateSubscriptionModal } from '@/components/financial/CreateSubscriptionModal';
import { SubscriptionDetailsModal } from '@/components/financial/SubscriptionDetailsModal';
import { useAssinaturaActions } from '@/hooks/useAssinaturaActions';

const logger = createLogger('FinancialSubscriptions');

/* Helpers/labels/cores movidos para `SubscriptionsTable.tsx` para reuso */
import {
  SubscriptionsTable,
  statusLabels,
  FREQ_LABEL,
} from '@/components/financial/SubscriptionsTable';




export default function FinancialSubscriptions() {
  // ====== Filtros ======
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [frequenciaFilter, setFrequenciaFilter] = useState<string>('all');
  const [meioPagamentoFilter, setMeioPagamentoFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // ====== Paginação ======
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // ====== Modais ======
  const [createOpen, setCreateOpen] = useState(false);
  // Auto-abre modal quando vindo do botão 'Novo' do header (Quick Create)
  useAutoOpenFromQuery('1', () => setCreateOpen(true));
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedAssinaturaId, setSelectedAssinaturaId] = useState<string | null>(null);

  // ====== Hooks ======
  const queryClient = useQueryClient();
  const { currentTenant } = useAuth();
  const { meios } = useFinanceiroMeiosPagamento();
  const { hasFinancialModule, isLoadingLimits } = useResourceLimits();
  const navigate = useNavigate();

  // ====== Limites (compartilha o contador de cobranças, mesmo padrão da página de Cobranças) ======
  const { data: chargeCount = 0 } = useQuery({
    queryKey: ['financeiro-cobrancas-count', currentTenant?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('count_active_financeiro_cobrancas' as any);
      if (error) {
        logger.error('Erro ao contar cobranças ativas', { error });
        return 0;
      }
      return data as number;
    },
    staleTime: 30000,
  });

  const isAtLimit = !hasFinancialModule && chargeCount >= 10;

  // ====== Debounce da busca ======
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // ====== Filtros ======
  const filters: AssinaturaPaginatedFilters = useMemo(
    () => ({
      status: statusFilter !== 'all' ? statusFilter : null,
      frequencia: frequenciaFilter !== 'all' ? frequenciaFilter : null,
      meio_pagamento_id: meioPagamentoFilter !== 'all' ? meioPagamentoFilter : null,
      search: debouncedSearch || null,
    }),
    [statusFilter, frequenciaFilter, meioPagamentoFilter, debouncedSearch],
  );

  const { assinaturas, totalCount, kpis, isLoading, isFetching, refetch } =
    useFinanceiroAssinaturasPaginated(filters, { page: currentPage, pageSize });

  const { isRefreshing, triggerRefresh } = useRefreshWithTimestamp();

  const handleRefresh = useCallback(() => {
    triggerRefresh(async () => {
      refetch();
      // v1.0.1 — também invalida cache dos detalhes (modal de recorrência),
      // garantindo que ao reabrir o modal os ciclos venham atualizados.
      queryClient.invalidateQueries({ queryKey: ['assinatura-detalhes'] });
    });
  }, [triggerRefresh, refetch, queryClient]);

  // ====== Realtime: invalida cache em mudanças de assinaturas/cobranças ======
  useEffect(() => {
    if (!currentTenant?.id) return;
    const channel = supabase
      .channel(`financeiro-assinaturas-rt-${currentTenant.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'financeiro_assinaturas',
          filter: `tenant_id=eq.${currentTenant.id}`,
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: ['financeiro-assinaturas-paginated', currentTenant.id],
          });
        },
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'financeiro_cobrancas',
          filter: `tenant_id=eq.${currentTenant.id}`,
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: ['financeiro-assinaturas-paginated', currentTenant.id],
          });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentTenant?.id, queryClient]);

  // Reset página ao mudar filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, frequenciaFilter, meioPagamentoFilter]);

  const hasActiveFilters =
    !!search || statusFilter !== 'all' || frequenciaFilter !== 'all' || meioPagamentoFilter !== 'all';

  const clearFilters = useCallback(() => {
    setSearch('');
    setStatusFilter('all');
    setFrequenciaFilter('all');
    setMeioPagamentoFilter('all');
  }, []);

  const formatCurrency = (v: number, moeda = 'BRL') =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: moeda || 'BRL' }).format(v || 0);

  const handleRowClick = (a: PaginatedAssinatura) => {
    setSelectedAssinaturaId(a.id);
    setDetailsOpen(true);
  };

  return (
    <TooltipProvider>
      <div className="flex flex-col h-full">
        {/* ====== Header Compacto ====== */}
        <div className="flex flex-col gap-3 p-4 border-b sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center justify-between gap-2 sm:block">
            <div>
              <h1 className="text-xl font-semibold">Recorrência</h1>
              <p className="hidden sm:block text-xs text-muted-foreground">
                Assinaturas e cobranças automáticas com ciclos
              </p>
            </div>
            {/* Ações (mobile) */}
            <div className="flex items-center gap-1.5 sm:hidden">
              <Button
                variant="outline"
                size="icon-sm"
                onClick={handleRefresh}
                disabled={isRefreshing}
                title="Atualizar dados"
              >
                <RefreshCw className={cn('h-3.5 w-3.5', isRefreshing && 'animate-spin')} />
              </Button>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      size="sm"
                      onClick={() => setCreateOpen(true)}
                      disabled={isAtLimit}
                      className="h-8 text-xs gap-1.5"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">Nova Recorrência</span>
                    </Button>
                  </span>
                </TooltipTrigger>
                {isAtLimit && (
                  <TooltipContent>
                    Limite de 10 cobranças atingido. Contrate o módulo Financeiro.
                  </TooltipContent>
                )}
              </Tooltip>
            </div>
          </div>

          {/* Ações (desktop) */}
          <div className="hidden sm:flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="icon-sm"
              onClick={handleRefresh}
              disabled={isRefreshing}
              title="Atualizar dados"
            >
              <RefreshCw className={cn('h-3.5 w-3.5', isRefreshing && 'animate-spin')} />
            </Button>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    size="sm"
                    onClick={() => setCreateOpen(true)}
                    disabled={isAtLimit}
                    className="h-8 text-xs gap-1.5"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Nova Recorrência
                  </Button>
                </span>
              </TooltipTrigger>
              {isAtLimit && (
                <TooltipContent>
                  Limite de 10 cobranças atingido. Contrate o módulo Financeiro.
                </TooltipContent>
              )}
            </Tooltip>
          </div>
        </div>

        {/* ====== KPI Cards ====== */}
        {!isLoading && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-4 py-3 border-b">
            <Card className="border-0 shadow-none transition-all hover:shadow-md bg-purple-100 dark:bg-purple-900/30">
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Repeat className="h-5 w-5 shrink-0 text-purple-700 dark:text-purple-300" />
                    <p className="text-xs font-medium truncate">Total Recorrências</p>
                  </div>
                  <p className="text-xl font-bold leading-tight shrink-0">{kpis.total}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-none transition-all hover:shadow-md bg-lime-100 dark:bg-green-900/30">
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <CheckCircle2 className="h-5 w-5 shrink-0 text-green-700 dark:text-green-300" />
                    <p className="text-xs font-medium truncate">Ativas</p>
                  </div>
                  <p className="text-xl font-bold leading-tight shrink-0">{kpis.ativas}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-none transition-all hover:shadow-md bg-blue-100 dark:bg-blue-900/30">
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <TrendingUp className="h-5 w-5 shrink-0 text-blue-700 dark:text-blue-300" />
                    <p className="text-xs font-medium truncate">MRR estimado</p>
                  </div>
                  <p className="text-xl font-bold leading-tight shrink-0">
                    {formatCurrency(kpis.mrr)}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-none transition-all hover:shadow-md bg-cyan-100 dark:bg-cyan-900/30">
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Clock className="h-5 w-5 shrink-0 text-cyan-700 dark:text-cyan-300" />
                    <p className="text-xs font-medium truncate">Ciclos pendentes</p>
                  </div>
                  <p className="text-xl font-bold leading-tight shrink-0">
                    {kpis.ciclosPendentes}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ====== Banner — Módulo Financeiro gratuito limitado (componente compartilhado) ====== */}
        <FreeTierLimitBanner entityLabel="recorrências" />


        {/* ====== Barra de Filtros ====== */}
        <div className="flex flex-wrap items-center gap-1 p-2 sm:gap-2 sm:p-3 border-b bg-muted/30">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 sm:h-3.5 sm:w-3.5 text-muted-foreground" />
            <Input
              placeholder="Cliente, CPF/CNPJ, descrição..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8 h-6 text-[11px] sm:h-8 sm:text-sm"
            />
          </div>

          <Select value={statusFilter} onValueChange={v => setStatusFilter(v)}>
            <SelectTrigger className="w-[100px] h-6 text-[11px] sm:w-[140px] sm:h-8 sm:text-sm">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos status</SelectItem>
              {Object.entries(statusLabels).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={frequenciaFilter} onValueChange={v => setFrequenciaFilter(v)}>
            <SelectTrigger className="w-[110px] h-6 text-[11px] sm:w-[150px] sm:h-8 sm:text-sm">
              <SelectValue placeholder="Frequência" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas frequências</SelectItem>
              {Object.entries(FREQ_LABEL).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={meioPagamentoFilter} onValueChange={v => setMeioPagamentoFilter(v)}>
            <SelectTrigger className="w-[110px] h-6 text-[11px] sm:w-[160px] sm:h-8 sm:text-sm">
              <SelectValue placeholder="Meio Pgto" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos meios</SelectItem>
              {meios.map(m => (
                <SelectItem key={m.id} value={m.id}>
                  {m.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3 mr-1" />
              Limpar
            </Button>
          )}

          {isFetching && !isLoading && (
            <span className="text-xs text-muted-foreground animate-pulse">Atualizando...</span>
          )}
        </div>

        {/* ====== Tabela (componente reutilizável) — com setinhas de scroll horizontal ====== */}
        <ScrollIndicators className="flex-1">
          <div className="h-full w-full overflow-hidden">
            <SubscriptionsTable
              assinaturas={assinaturas}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              formatCurrency={formatCurrency}
              emptyHint={
                hasActiveFilters
                  ? 'Tente ajustar os filtros de busca'
                  : 'Clique em "Nova Recorrência" para começar'
              }
            />
          </div>
        </ScrollIndicators>


        {/* ====== Footer ====== */}
        {!isLoading && totalCount > 0 && (
          <div className="border-t px-4 py-2 bg-background">
            <PaginationFooter
              totalItems={totalCount}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={size => {
                setPageSize(size);
                setCurrentPage(1);
              }}
              pageSizeOptions={[10, 20, 50, 100]}
              borderClassName=""
              className="mt-0 pt-0"
            />
          </div>
        )}

        {/* Modais */}
        <CreateSubscriptionModal open={createOpen} onOpenChange={setCreateOpen} />
        <SubscriptionDetailsModal
          assinaturaId={selectedAssinaturaId}
          open={detailsOpen}
          onOpenChange={v => {
            setDetailsOpen(v);
            if (!v) setSelectedAssinaturaId(null);
          }}
        />
      </div>
    </TooltipProvider>
  );
}

/* `SubscriptionRow` foi extraído para `src/components/financial/SubscriptionsTable.tsx`
 * e é renderizado internamente por `<SubscriptionsTable />`. */

